import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService, LanguageService, Language, environment } from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { HttpClient } from "@angular/common/http";
import { AppService } from "@mypxplat/xplat/web/core";
import { GenericAlertComponent } from "../../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-admin-event-form",
  templateUrl: "admin-event-form.component.html",
  standalone: false,
})
export class AdminEventFormComponent extends BaseComponent implements OnInit {
  @Input() event: any = null;
  @Input() addOccurrenceMode: boolean = false;
  @Input() isChildMode: boolean = false;
  @Input() isTemplateMode: boolean = false;
  @Input() parentEvent: any = null;
  /**
   * Start datetimes of the occurrences this series already has, so the "this will create N events"
   * count only counts the dates that are actually missing. The manager already has them loaded;
   * an empty list just makes the count the whole future schedule.
   */
  @Input() existingOccurrenceDates: string[] = [];
  @Output() saved: EventEmitter<any> = new EventEmitter();

  public isNew: boolean = true;
  public saving: boolean = false;
  public dateError: string = "";
  public uploading: boolean = false;

  // Form fields
  public title: string = "";
  public slug: string = "";
  public description: string = "";
  public startDatetime: string = "";
  public endDatetime: string = "";
  public timezone: string = "America/Chicago";
  public imageUrl: string = "";
  // Livestream platform picker: "" means this isn't a livestream event (all ID fields hidden
  // on the form and sent as empty on save). "vimeo" shows the two Vimeo ID inputs. "youtube"
  // shows a single YouTube Video ID input — the server enriches channel + live_chat_id from
  // the YouTube Data API on save so admins only enter the one thing they can find on YT.
  public livestreamPlatform: "" | "vimeo" | "youtube" = "";
  public vimeoLivestreamId: string = "";
  public vimeoVideoId: string = "";
  public youtubeVideoId: string = "";
  /** Trailer/preview YouTube id — powers the "Watch The Trailer" CTA on the countdown. */
  public previewVideoId: string = "";
  public address: string = "";
  public city: string = "";
  public state: string = "";
  public zip: string = "";
  public country: string = "";
  public email: string = "";
  public website: string = "";
  public online: boolean = true;
  public fspExclusive: boolean = false;
  public commentsEnabled: boolean = true;
  // Embed panel features (DENG-893). Per-occurrence, like commentsEnabled; default off.
  public shopEnabled: boolean = false;
  public signupEnabled: boolean = false;
  /** Hide from MyFender (web + mobile calendar/listings/detail) while still serving the fender.com embed. */
  public hideFromPortal: boolean = false;
  /**
   * Per-state sign-up config (DENG-930). Marketing wants the form to behave differently before the
   * stream (countdown) vs during it (livestream): each state has its own Iterable list, source tag,
   * button label, form prompt, and success message.
   */
  public signupConfig: {
    countdown: {
      list_id: string;
      source: string;
      headline: string;
      button_text: string;
      submit_button_text: string;
      form_message: string;
      optin_message: string;
      optin_required: boolean;
      optin_required_message: string;
      success_message: string;
      success_header: string;
      legal_disclaimer: string;
    };
    livestream: {
      list_id: string;
      source: string;
      headline: string;
      button_text: string;
      submit_button_text: string;
      form_message: string;
      optin_message: string;
      optin_required: boolean;
      optin_required_message: string;
      success_message: string;
      success_header: string;
      legal_disclaimer: string;
    };
  } = {
    countdown: {
      list_id: "",
      source: "",
      headline: "",
      button_text: "",
      submit_button_text: "",
      form_message: "",
      optin_message: "",
      optin_required: false,
      optin_required_message: "",
      success_message: "",
      success_header: "",
      legal_disclaimer: "",
    },
    livestream: {
      list_id: "",
      source: "",
      headline: "",
      button_text: "",
      submit_button_text: "",
      form_message: "",
      optin_message: "",
      optin_required: false,
      optin_required_message: "",
      success_message: "",
      success_header: "",
      legal_disclaimer: "",
    },
  };
  /** Which sign-up state the admin is currently editing (segmented bar); the other is kept in memory. */
  public signupState: "countdown" | "livestream" = "countdown";
  public translating: boolean = false;

  /**
   * Per-language event descriptions (`{ en, fr, … }`). English lives in the main `description` field;
   * this holds the other languages, shown behind the "Show translations" toggle (mirrors the PDP
   * localized-descriptions editor). Edited values are persisted on save (backend merges the map).
   */
  public descriptionsByLang: Record<string, string> = {};
  public otherLanguages: Language[] = [];
  public showTranslations: boolean = false;
  /** Quill only ingests `content` on init — drop briefly to force the per-lang editors to re-read. */
  public quillReady: boolean = true;
  public destinations: string[] = [];
  public tagsString: string = "";
  public status: string = "draft";

  // Embed destinations an event can appear on (fender.com iframe, later play.fender.com, etc.)
  public knownDestinations: Array<{ id: string; label: string }> = [
    { id: "fender.com", label: "Fender.com" },
    { id: "play.fender.com", label: "Play.fender.com" },
  ];

  // Tracking manual edits
  private slugManuallyEdited: boolean = false;
  private endDateManuallySet: boolean = false;

  public isRecurring: boolean = false;
  public recurringFrequency: string = "weekly";
  public recurringInterval: number = 1;
  public recurringEndDate: string = "";
  public monthlyPattern: string = "date";

  public monthlyPatternLabel: string = "";

  /**
   * True when the event being edited is an existing series (a template, or the childless series
   * an intermittent event starts life as) — its schedule is then editable in place. Kept separate
   * from `isRecurring`, which means "the admin ticked Recurring while creating a new event" and
   * also gates the livestream picker.
   */
  public editingRecurrence: boolean = false;
  /** Snapshot of the schedule as loaded, so save can tell whether the admin actually changed it. */
  private originalRecurringSignature: string = "";
  public recurringInstanceCount: number = 0;

  public timezones: string[] = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
    "Europe/London",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Rome",
    "Europe/Istanbul",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Asia/Seoul",
    "Australia/Sydney",
  ];

  public copiedDestination: string = "";

  toggleDestination(id: string) {
    if (this.destinations.includes(id)) {
      this.destinations = this.destinations.filter((d) => d !== id);
    } else {
      this.destinations = [...this.destinations, id];
    }
  }

  /** The iframe snippet a site owner pastes to embed this destination's livestream. */
  embedCode(destination: string): string {
    const base = (environment.fenderUrl || "https://my.fender.com/").replace(/\/+$/, "");
    return `<iframe src="${base}/embed/livestream?destination=${destination}" style="width:100%;height:100%;border:0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
  }

  copyEmbedCode(destination: string) {
    const code = this.embedCode(destination);
    const done = () => {
      this.copiedDestination = destination;
      setTimeout(() => {
        if (this.copiedDestination === destination) this.copiedDestination = "";
      }, 2000);
    };
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done, () => {});
    } else {
      // Fallback for insecure contexts / older browsers.
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        done();
      } catch (e) {}
      document.body.removeChild(ta);
    }
  }

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public eventsService: EventsService,
    private http: HttpClient,
    public appService: AppService,
    public translate: TranslateService,
    private languageService: LanguageService
  ) {
    super(userService);
  }

  ngOnInit() {
    // Non-English languages for the "Show translations" section (English is the main field).
    this.otherLanguages = this.languageService.languages.filter((lang) => lang.code !== "en");
    // Seed the per-language description map from the event (an object keyed by lang code); legacy
    // string descriptions start with an empty map (only English, in the main field).
    const desc = this.event?.description;
    this.descriptionsByLang = desc && typeof desc === "object" ? { ...desc } : {};
    if (this.addOccurrenceMode && this.parentEvent) {
      this.isNew = true;
      this.title = this.parentEvent.title?.en || this.parentEvent.title || this.parentEvent._displayTitle || "";
      this.timezone = this.parentEvent.timezone || "America/Chicago";
      this.status = this.parentEvent.status || "draft";
      this.description = this.parentEvent.description?.en || this.parentEvent.description || "";
      this.imageUrl = this.parentEvent.image_url || "";
      return;
    }
    if (this.event) {
      this.isNew = false;
      this.endDateManuallySet = true;
      this.title = this.event.title?.en || this.event.title || "";
      this.slug = this.event.slug || "";
      this.description = this.event.description?.en || this.event.description || "";
      this.startDatetime = this.formatDatetimeForInput(this.event.start_datetime);
      this.endDatetime = this.formatDatetimeForInput(this.event.end_datetime);
      this.timezone = this.event.timezone || "America/Chicago";
      this.imageUrl = this.event.image_url || "";
      this.vimeoLivestreamId = this.event.vimeo_livestream_id || "";
      this.vimeoVideoId = this.event.vimeo_video_id || "";
      this.youtubeVideoId = this.event.youtube_video_id || "";
      this.previewVideoId = this.event.preview_video_id || "";
      // Read livestream_platform if the server sent it; otherwise fall back to the legacy
      // "populated vimeo id = it's a vimeo livestream" heuristic. Keeps old events editable
      // without any migration step.
      if (this.event.livestream_platform === "vimeo" || this.event.livestream_platform === "youtube") {
        this.livestreamPlatform = this.event.livestream_platform;
      } else if (this.event.vimeo_livestream_id) {
        this.livestreamPlatform = "vimeo";
      } else {
        this.livestreamPlatform = "";
      }
      this.address = this.event.address || "";
      this.city = this.event.city || "";
      this.state = this.event.state || "";
      this.zip = this.event.zip || "";
      this.country = this.event.country || "";
      this.email = this.event.email || "";
      this.website = this.event.website || "";
      this.online = this.event.online || false;
      this.fspExclusive = this.event.fsp_exclusive || false;
      this.commentsEnabled = this.event.comments_enabled !== false;
      this.shopEnabled = this.event.shop_enabled === true;
      this.signupEnabled = this.event.signup_enabled === true;
      this.hideFromPortal = this.event.hide_from_portal === true;
      this.loadSignupConfig(this.event.signup_config);
      this.destinations = this.event.destinations || [];
      this.tagsString = (this.event.tags || []).join(", ");
      this.status = this.event.status || "draft";
      this.loadRecurringRule();
    }
    if (this.endDatetime) this.endDateManuallySet = true;
    this.updateMonthlyPatternLabel();
  }

  /**
   * Hydrates the schedule editor from an existing series. The rule only ever lives on the template
   * (occurrences carry `recurring_rule: null`), so editing the schedule is offered exactly where
   * the rule is.
   */
  private loadRecurringRule() {
    const rule = this.event?.recurring_rule;
    if (!rule) return;
    this.editingRecurrence = true;
    this.recurringFrequency = rule.frequency || "weekly";
    this.recurringInterval = rule.interval || 1;
    this.recurringEndDate = (rule.end_date || "").slice(0, 10);
    this.monthlyPattern = rule.monthly_pattern || "date";
    this.recurringInstanceCount = rule.instance_count || 0;
    this.originalRecurringSignature = this.recurringSignature();
    this.recountPendingOccurrences();
  }

  /** Identity of the current schedule, for change detection on save. */
  private recurringSignature(): string {
    return [this.recurringFrequency, this.recurringInterval, this.recurringEndDate, this.recurringFrequency === "monthly" ? this.monthlyPattern : ""].join("|");
  }

  private get recurringScheduleChanged(): boolean {
    return this.editingRecurrence && this.recurringSignature() !== this.originalRecurringSignature;
  }

  /** Mirrors `recurringScheduleChanged` as a plain field for the template's heads-up notice. */
  public scheduleDirty: boolean = false;
  /** How many occurrences the currently-entered schedule would create. */
  public pendingOccurrenceCount: number = 0;
  /** True when that count exceeds what one save can create. */
  public exceedsOccurrenceCap: boolean = false;
  public occurrenceCap: number = 0;

  onRecurringSettingChange() {
    this.updateMonthlyPatternLabel();
    this.scheduleDirty = this.recurringScheduleChanged;
    this.recountPendingOccurrences();
  }

  /**
   * Recomputes what the entered schedule would produce. Runs on every schedule edit so the admin
   * sees the size of the fan-out — and the per-save cap — before committing to it.
   */
  private recountPendingOccurrences() {
    this.occurrenceCap = this.eventsService.maxOccurrencesPerSave;
    if (!this.recurringFrequency || this.recurringFrequency === "intermittent" || !this.recurringEndDate || !this.startDatetime) {
      this.pendingOccurrenceCount = 0;
      this.exceedsOccurrenceCap = false;
      return;
    }
    this.pendingOccurrenceCount = this.eventsService.countNewRecurringOccurrences(
      this.startDatetime,
      this.endDatetime || this.startDatetime,
      this.buildRecurringRule(),
      this.editingRecurrence ? this.existingOccurrenceDates : [],
      this.timezone
    );
    this.exceedsOccurrenceCap = this.pendingOccurrenceCount > this.occurrenceCap;
  }

  /** Plain-English schedule, e.g. "monthly on the 2nd Wednesday" — used in the confirm copy. */
  private describeSchedule(): string {
    const base = {
      daily: "every day",
      weekly: "every week",
      biweekly: "every two weeks",
      monthly: "every month",
      intermittent: "on dates you add by hand",
    }[this.recurringFrequency];
    if (this.recurringFrequency === "monthly" && this.monthlyPattern === "weekday" && this.monthlyPatternLabel) {
      return `every month on the ${this.monthlyPatternLabel}`;
    }
    return base || this.recurringFrequency;
  }

  /**
   * Explains what saving a changed schedule will do before it happens. Deliberately spells out
   * that nothing existing is moved or removed — the fan-out is add-only, so an admin extending a
   * series doesn't have to worry about the per-occurrence video IDs already set on it.
   */
  private confirmRecurringChange(): Promise<boolean> {
    // Recount here rather than trusting the last input event — the copy quotes a number, and a
    // stale one would promise the wrong thing.
    this.recountPendingOccurrences();
    const endsOn = this.recurringEndDate ? this.formatDateLabel(this.recurringEndDate) : "";
    const count = this.pendingOccurrenceCount;
    const lines = [`<p>You changed the schedule for this series to <b>${this.describeSchedule()}</b>${endsOn ? ` through <b>${endsOn}</b>` : ""}.</p>`];
    if (this.exceedsOccurrenceCap) {
      lines.push(
        `<p>That schedule needs <b>${count} more occurrences</b>, and we create a maximum of <b>${this.occurrenceCap}</b> at a time. ` +
          `Saving will create the next ${this.occurrenceCap} and end the series on the last one created — come back and extend the end date again to add the rest.</p>`
      );
    } else {
      lines.push(`<p>Saving will create <b>${count} ${count === 1 ? "occurrence" : "occurrences"}</b> between now and the end date.</p>`);
    }
    lines.push("<p>Occurrences that already exist stay exactly as they are — nothing is moved, rescheduled, or deleted, and past dates are left alone.</p>");
    return this.appService.confirm(lines.join(""), "Save & Create Events", "Cancel") as Promise<boolean>;
  }

  private formatDateLabel(date: string): string {
    const [y, mo, d] = date.split("-").map(Number);
    if (!y || !mo || !d) return date;
    return new Date(y, mo - 1, d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  get showSharedFields(): boolean {
    return !this.isChildMode && !this.addOccurrenceMode;
  }

  /**
   * Client-side validation for the livestream section: Vimeo needs both IDs. YouTube only needs
   * the one video id. "" (not a livestream) always valid. Used to gate the Save button.
   */
  get livestreamFieldsValid(): boolean {
    if (this.livestreamPlatform === "vimeo") {
      return (!this.vimeoLivestreamId && !this.vimeoVideoId) || (!!this.vimeoLivestreamId && !!this.vimeoVideoId);
    }
    return true;
  }

  /**
   * Builds the livestream slice of the request body. The server (`resolveLivestreamFields` in
   * eventController) clears the non-selected platform's IDs and enriches YouTube — we still send
   * only the selected platform's fields to keep the payload honest and small.
   */
  private buildLivestreamPayload(): Record<string, string> {
    if (this.livestreamPlatform === "vimeo") {
      return {
        livestream_platform: "vimeo",
        vimeo_livestream_id: this.vimeoLivestreamId,
        vimeo_video_id: this.vimeoVideoId,
      };
    }
    if (this.livestreamPlatform === "youtube") {
      return {
        livestream_platform: "youtube",
        youtube_video_id: this.youtubeVideoId,
      };
    }
    return { livestream_platform: "" };
  }

  onTitleChange() {
    if (this.isNew && !this.slugManuallyEdited) {
      this.slug = this.generateSlug(this.title);
    }
  }

  onSlugInput() {
    this.slugManuallyEdited = true;
  }

  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .substring(0, 80);
  }

  onStartDateChange() {
    this.updateMonthlyPatternLabel();
    this.recountPendingOccurrences();
    if (!this.endDateManuallySet && this.startDatetime) {
      const start = new Date(this.startDatetime);
      start.setHours(start.getHours() + 1);
      const y = start.getFullYear();
      const mo = String(start.getMonth() + 1).padStart(2, "0");
      const d = String(start.getDate()).padStart(2, "0");
      const h = String(start.getHours()).padStart(2, "0");
      const mi = String(start.getMinutes()).padStart(2, "0");
      this.endDatetime = `${y}-${mo}-${d}T${h}:${mi}`;
    }
    this.validateDateRange();
  }

  onEndDateChange() {
    this.endDateManuallySet = true;
    this.validateDateRange();
  }

  private validateDateRange() {
    if (this.startDatetime && this.endDatetime && this.endDatetime <= this.startDatetime) {
      this.dateError = this.translate.instant("admin_portal.end_time_after_start");
    } else {
      this.dateError = "";
    }
  }

  /**
   * Auto-translate the saved event's title & description into all supported languages (OpenAI).
   * Reads the PERSISTED English copy, so the admin should save their English edits first. Missing
   * languages fall back to English at read time (see eventController.toPublicEvent).
   */
  autoTranslateDescription() {
    if (!this.event?.id || this.translating) return;
    this.translating = true;
    this.eventsService.adminTranslateEvent(this.event.id).subscribe({
      next: (res: any) => {
        this.translating = false;
        // The endpoint returns the fresh per-language maps — load them into the editors so the admin
        // can review/tweak and Save (the translation is already persisted; Save flushes any edits).
        if (res?.description && typeof res.description === "object") {
          this.descriptionsByLang = { ...res.description };
          if (res.description.en) this.description = res.description.en;
          this.showTranslations = true;
          this.refreshQuillEditors();
        }
        this.appService.alert("Auto-translated the title & description into all supported languages. Review and Save. Missing languages fall back to English.");
      },
      error: (err) => {
        this.translating = false;
        this.appService.alertError(err);
      },
    });
  }

  /** Full per-language description map for a save payload — the main field is the authoritative English. */
  private buildDescriptionPayload(): Record<string, string> {
    return { ...this.descriptionsByLang, en: this.description };
  }

  /**
   * Seed the per-state sign-up editors from a saved `signup_config` (either state may be absent).
   * The admin edits English only; the text fields are stored server-side as per-language maps
   * (`{ en, es, … }`, like title/description) and auto-translated on save, so we extract `.en` here.
   * A plain-string value is tolerated for backward compatibility.
   */
  private loadSignupConfig(saved: any) {
    const en = (v: any): string => (v && typeof v === "object" ? v.en || "" : v || "");
    const forState = (state: "countdown" | "livestream") => ({
      list_id: saved?.[state]?.list_id || "",
      source: saved?.[state]?.source || "",
      headline: en(saved?.[state]?.headline),
      button_text: en(saved?.[state]?.button_text),
      submit_button_text: en(saved?.[state]?.submit_button_text),
      form_message: en(saved?.[state]?.form_message),
      optin_message: en(saved?.[state]?.optin_message),
      optin_required: !!saved?.[state]?.optin_required,
      optin_required_message: en(saved?.[state]?.optin_required_message),
      success_message: en(saved?.[state]?.success_message),
      success_header: en(saved?.[state]?.success_header),
      legal_disclaimer: en(saved?.[state]?.legal_disclaimer),
    });
    this.signupConfig = { countdown: forState("countdown"), livestream: forState("livestream") };
  }

  /** Segmented-bar handler: switch which sign-up state's fields are shown. */
  switchSignupState(args: { id: string }) {
    this.signupState = args.id === "livestream" ? "livestream" : "countdown";
  }

  /** The per-state sign-up config as sent to the API; the server normalizes/merges it. */
  private buildSignupConfigPayload() {
    return {
      countdown: { ...this.signupConfig.countdown },
      livestream: { ...this.signupConfig.livestream },
    };
  }

  /** Force the per-language Quill editors to re-ingest their `content` (they only read it on init). */
  private refreshQuillEditors() {
    this.quillReady = false;
    setTimeout(() => (this.quillReady = true));
  }

  findVimeoIdsInstructions(livestreamId?: boolean) {
    const modalRef = this.appService.showModal(GenericAlertComponent, { size: "lg" });
    modalRef.componentInstance.title = "Vimeo Livestream IDs";
    let html = `
          <div class="m-y-10">
            Find the Vimeo Livestream ID by clicking on the livestream in the Vimeo dashboard, then copy the ID from the URL.
            <br /><br />
            <img src="./assets/images/vimeo-eventid.png" style="width: 100%; height: auto;" />
          </div>
        `;
    if (!livestreamId) {
      html = `
        <div class="m-y-10">
          Find the Vimeo Livestream Clip ID by visiting the livestream in the Vimeo dashboard, open developer tools, click on the network tab, and find a request with clip_id=1234 in the url and copy that ID.
          <br /><br />
          <img src="./assets/images/vimeo-clip-id.png" style="width: 100%; height: auto;" />
        </div>
      `;
    }
    modalRef.componentInstance.html = html;
  }

  updateMonthlyPatternLabel() {
    if (!this.startDatetime) {
      this.monthlyPatternLabel = "";
      return;
    }
    const d = new Date(this.startDatetime);
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const nth = Math.ceil(d.getDate() / 7);
    const ordinals = ["", "1st", "2nd", "3rd", "4th", "5th"];
    this.monthlyPatternLabel = `${ordinals[nth]} ${dayNames[d.getDay()]}`;
  }

  onFileSelected(fileEvent: any) {
    const file = fileEvent.target.files[0];
    if (!file) return;

    this.uploading = true;
    const eventId = this.event?.id || "temp";
    this.eventsService.adminUploadEventImage(eventId, file.name, file.type).subscribe({
      next: (result: any) => {
        this.http.put(result.upload_url, file, { headers: { "Content-Type": file.type } }).subscribe({
          next: () => {
            this.imageUrl = result.url;
            this.uploading = false;
          },
          error: () => {
            this.uploading = false;
          },
        });
      },
      error: () => {
        this.uploading = false;
      },
    });
  }

  removeImage() {
    this.imageUrl = "";
  }

  save() {
    if (this.uploading) return;
    this.dateError = "";

    if (this.isNew && this.startDatetime && !this.isTemplateMode) {
      const startDate = new Date(this.startDatetime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate < today) {
        this.dateError = "Cannot create events in the past.";
        return;
      }
    }

    // Add occurrence mode
    if (this.addOccurrenceMode && this.parentEvent) {
      if (!this.startDatetime) return;
      this.saving = true;
      const parentId = this.parentEvent.parent_event_id || this.parentEvent.id;
      const tags = this.tagsString
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      const occurrenceData: any = {
        start_datetime: this.startDatetime,
        end_datetime: this.endDatetime || this.startDatetime,
        timezone: this.timezone,
        description: this.buildDescriptionPayload(),
        ...this.buildLivestreamPayload(),
        image_url: this.imageUrl,
        comments_enabled: this.commentsEnabled,
        shop_enabled: this.shopEnabled,
        signup_enabled: this.signupEnabled,
        hide_from_portal: this.hideFromPortal,
        preview_video_id: this.previewVideoId,
        signup_config: this.buildSignupConfigPayload(),
        tags,
        status: this.status,
      };
      this.eventsService.adminAddOccurrence(parentId, occurrenceData).subscribe({
        next: () => {
          this.saving = false;
          this.saved.emit();
          this.activeModal.close();
        },
        error: () => {
          this.saving = false;
        },
      });
      return;
    }

    // Child mode: only update occurrence-specific fields
    if (this.isChildMode && this.event) {
      if (!this.startDatetime) return;
      this.saving = true;
      const tags = this.tagsString
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      const eventData: any = {
        start_datetime: this.startDatetime,
        end_datetime: this.endDatetime || this.startDatetime,
        timezone: this.timezone,
        description: this.buildDescriptionPayload(),
        ...this.buildLivestreamPayload(),
        image_url: this.imageUrl,
        comments_enabled: this.commentsEnabled,
        shop_enabled: this.shopEnabled,
        signup_enabled: this.signupEnabled,
        hide_from_portal: this.hideFromPortal,
        preview_video_id: this.previewVideoId,
        signup_config: this.buildSignupConfigPayload(),
        tags,
        status: this.status,
      };
      this.eventsService.adminUpdateEvent(this.event.id, eventData).subscribe({
        next: () => {
          this.saving = false;
          this.saved.emit();
          this.activeModal.close();
        },
        error: () => {
          this.saving = false;
        },
      });
      return;
    }

    // Template or standalone mode: all fields
    if (!this.title) return;
    if (!this.isTemplateMode && !this.startDatetime) return;

    this.saving = true;
    const tags = this.tagsString
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);

    const eventData: any = {
      title: this.title,
      slug: this.slug,
      description: this.buildDescriptionPayload(),
      timezone: this.timezone,
      image_url: this.imageUrl,
      ...this.buildLivestreamPayload(),
      address: this.address,
      city: this.city,
      state: this.state,
      zip: this.zip,
      country: this.country,
      email: this.email,
      website: this.website,
      online: this.online,
      fsp_exclusive: this.fspExclusive,
      comments_enabled: this.commentsEnabled,
      shop_enabled: this.shopEnabled,
      signup_enabled: this.signupEnabled,
      hide_from_portal: this.hideFromPortal,
      preview_video_id: this.previewVideoId,
      signup_config: this.buildSignupConfigPayload(),
      destinations: this.destinations,
      tags,
    };

    if (!this.isTemplateMode) {
      eventData.status = this.status;
    }

    if (!this.isTemplateMode) {
      eventData.start_datetime = this.startDatetime;
      eventData.end_datetime = this.endDatetime || this.startDatetime;
    }

    if (this.isNew && this.isRecurring) {
      eventData.start_datetime = this.startDatetime;
      eventData.end_datetime = this.endDatetime || this.startDatetime;
      eventData.recurring_rule = this.buildRecurringRule();
    }

    if (this.isNew) {
      this.eventsService.adminCreateEvent(eventData).subscribe({
        next: () => this.onSaved(),
        error: () => {
          this.saving = false;
        },
      });
      return;
    }

    // Editing an existing series: only send the schedule when the admin actually changed it, and
    // only after they've confirmed — saving a changed schedule creates events.
    if (this.recurringScheduleChanged) {
      eventData.recurring_rule = this.buildRecurringRule();
      eventData.regenerate_occurrences = true;
      this.saving = false;
      this.confirmRecurringChange().then((confirmed) => {
        if (!confirmed) return;
        this.saving = true;
        this.submitUpdate(eventData);
      });
      return;
    }

    this.submitUpdate(eventData);
  }

  private buildRecurringRule() {
    return {
      frequency: this.recurringFrequency,
      interval: this.recurringInterval || 1,
      end_date: this.recurringEndDate || "",
      monthly_pattern: this.recurringFrequency === "monthly" ? this.monthlyPattern : undefined,
    };
  }

  private submitUpdate(eventData: any) {
    this.eventsService.adminUpdateEvent(this.event.id, eventData).subscribe({
      next: (result: any) => this.onSaved(result),
      error: (error) => {
        this.saving = false;
        this.appService.alertError(error);
      },
    });
  }

  /**
   * Closes the form, and reports the fan-out when a schedule change created dates — otherwise the
   * only way to know how many occurrences appeared is to expand the series and count them.
   */
  private onSaved(result?: any) {
    this.saving = false;
    const created = result?.created_occurrences || 0;
    if (created) {
      const plural = created === 1 ? "occurrence" : "occurrences";
      const capped = result?.occurrence_cap_reached ? ` That's the maximum this save could add, so the series now ends on the last date created — extend the end date again to add more.` : "";
      this.appService.alert(`Created ${created} new ${plural} for this series.${capped}`);
    }
    this.saved.emit();
    this.activeModal.close();
  }

  private formatDatetimeForInput(datetime: string): string {
    if (!datetime) return "";
    return datetime.slice(0, 16);
  }
}
